/**
 * 1 組の最大人数 (REQ-28 / #1536)。
 *
 * **上限は店舗オーナー（または店長）が決める**。未設定の店は 50。
 * サーバ側の `services/pay/api/services/party_size.py` と同じ既定値で、正はサーバ。
 * ここは「押す前に止める・入力欄に上限を出す」ための表示用。
 *
 * 以前は画面ごとに `Math.min(99, …)` が散っていて、130 名で開いた卓の「＋」が
 * 黙って 99 に落ち、**伝票の数量まで 99 名分に書き換わって過少請求**になっていた。
 */
import type { Store } from '../store/useStore'

/** 店舗設定が無いときの上限。サーバの DEFAULT_MAX_PARTY_SIZE と揃えること。 */
export const DEFAULT_MAX_PARTY_SIZE = 50

/** 設定値そのものの天井。サーバの SETTING_UPPER_BOUND と揃えること。 */
export const MAX_PARTY_SIZE_SETTING_UPPER_BOUND = 999

/** この店の 1 組あたり最大人数。壊れた値なら既定へ倒す（上限が消える方が危険）。 */
export function maxPartySize(store?: Pick<Store, 'maxPartySize'> | null): number {
  const n = Number(store?.maxPartySize)
  if (!Number.isFinite(n) || n < 1 || n > MAX_PARTY_SIZE_SETTING_UPPER_BOUND) {
    return DEFAULT_MAX_PARTY_SIZE
  }
  return Math.floor(n)
}

/**
 * 放題プランの注文アイテムに付く id の prefix。サーバの
 * `services/all_you_can.py` の `PLAN_ITEM_PREFIX` と同じ値。
 *
 * frontend 2 箇所 + backend 1 箇所に同じ文字列が散っていたのでここへ寄せる。
 */
export const ALL_YOU_CAN_ITEM_PREFIX = 'all-you-can:'

export const isAllYouCanItem = (menuItemId: string | undefined): boolean =>
  !!menuItemId?.startsWith(ALL_YOU_CAN_ITEM_PREFIX)

/** 通常メニュー 1 行の数量上限。誤入力の桁あふれ（9999 個）を防ぐための値。 */
export const MAX_ITEM_QUANTITY = 99

/**
 * カート 1 行の数量上限。
 *
 * **放題プランは「数量 = 人数」**なので、店舗設定の 1 組の最大人数まで許す。
 * ここを 99 固定にしていたため、店舗が上限を 130 にしても飲み放題を 130 名で
 * カートに入れられなかった (#1583)。
 *
 * 通常メニューは従来どおり 99。上限を撤廃すると、ひつまぶし 9999 個のような
 * 誤入力がそのまま厨房と伝票へ流れる。
 */
export function maxQuantityFor(
  menuItemId: string | undefined,
  store?: Pick<Store, 'maxPartySize'> | null,
): number {
  return isAllYouCanItem(menuItemId) ? maxPartySize(store) : MAX_ITEM_QUANTITY
}
